import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DocAccordion from './components/DocAccordion';
import { documentsData } from '@/mocks/documents';
import type { DocItem } from '@/mocks/documents';
import { Link } from 'react-router-dom';

function getStoredDocs(): DocItem[] {
  try {
    const raw = localStorage.getItem('admin_docs');
    return raw !== null ? JSON.parse(raw) : documentsData;
  } catch {
    return documentsData;
  }
}

export default function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<DocItem[]>(() => getStoredDocs());
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  useEffect(() => {
    const handleStorage = () => setItems(getStoredDocs());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-56 md:h-72 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Kazakhstan%20military%20training%20ground%20tactical%20exercises%20soldiers%20in%20field%20uniforms%20dramatic%20natural%20light%20cinematic%20wide%20angle%20dust%20atmosphere%20dark%20dramatic%20tone%20no%20flags%20no%20text&width=1600&height=500&seq=docs-banner-01&orientation=landscape"
            alt="Documentation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/30" />
        </div>
        <div className="relative w-full px-4 md:px-12 pb-10">
          <div className="flex items-center gap-2 text-xs text-foreground-600 mb-3 font-label tracking-widest uppercase">
            <Link to="/" className="hover:text-accent-500 transition-colors">
              {t('nav.home', isEn ? 'Home' : isRu ? 'Главная' : 'Басты бет')}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400">
              {t('nav.documents', isEn ? 'Documentation' : isRu ? 'Документация' : 'Құжаттама')}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-foreground-50 uppercase tracking-widest">
            {isEn ? 'Documents' : isRu ? 'Документы' : 'Құжаттар'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <main className="w-full max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-px bg-accent-500" />
          <p className="text-xs font-label tracking-widest uppercase text-accent-500">
            {isEn ? 'Official Federation Documents' : isRu ? 'Официальные документы федерации' : 'Федерацияның ресми құжаттары'}
          </p>
        </div>

        <DocAccordion items={items} />
      </main>

      <Footer />
    </div>
  );
}